import {ValueObject} from '@s-ui/domain'

import {IdSharedValueObject} from '../../shared/ValueObjects/IdSharedValueObject.js'
import {BodyTypeSpecificationVehiclesValueObject} from './BodyTypeSpecificationVehiclesValueObject.js'
import {CubicCapacitySpecificationVehiclesValueObject} from './CubicCapacitySpecificationVehiclesValueObject.js'
import {DrivenWheelsSpecificationVehiclesValueObject} from './DrivenWheelsSpecificationVehiclesValueObject.js'
import {EnvironmentalLabelSpecificationsValueObject} from './EnvironmentalLabelSpecificationsValueObject.js'
import {FuelTypeSpecificationVehiclesValueObject} from './FuelTypeSpecificationVehiclesValueObject.js'
import {HpSpecificationVehiclesValueObject} from './HpSpecificationVehiclesValueObject.js'
import {KmSpecificationVehiclesValueObject} from './KmSpecificationVehiclesValueObject.js'
import {ListImagesValueObject} from './ListImagesValueObject.js'
import {MakeSpecificationVehiclesValueObject} from './MakeSpecificationVehiclesValueObject.js'
import {ModelSpecificationVehiclesValueObject} from './ModelSpecificationVehiclesValueObject.js'
import {ModelsSpecificationListVehiclesValueObject} from './ModelsSpecificationListVehiclesValueObject.js'
import {OfferTypeSpecificationVehiclesValueObject} from './OfferTypeSpecificationVehiclesValueObject.js'
import {PriceSpecificationValueObject} from './PriceSpecificationValueObject.js'
import {TransmissionTypeSpecificationVehiclesValueObject} from './TransmissionTypeSpecificationVehiclesValueObject.js'
import {YearSpecificationVehiclesValueObject} from './YearSpecificationVehiclesValueObject.js'

export class SpecificationsVehiclesValueObject extends ValueObject {
  #id
  #bodyType
  #cubicCapacity
  #drivenWheels
  #environmentalLabel
  #fuelType
  #hasStock
  #hp
  #images
  #isCertified
  #km
  #make
  #model
  #models
  #offerType
  #price
  #transmissionType
  #year

  constructor({
    id,
    bodyType,
    cubicCapacity,
    drivenWheels,
    environmentalLabel,
    fuelType,
    hasStock,
    hp,
    images,
    isCertified,
    km,
    make,
    model,
    models,
    offerType,
    price,
    transmissionType,
    year
  }) {
    super()

    this.#id = id
    this.#bodyType = bodyType
    this.#cubicCapacity = cubicCapacity
    this.#drivenWheels = drivenWheels
    this.#environmentalLabel = environmentalLabel
    this.#fuelType = fuelType
    this.#hasStock = hasStock
    this.#hp = hp
    this.#images = images
    this.#isCertified = isCertified
    this.#km = km
    this.#make = make
    this.#model = model
    this.#models = models
    this.#offerType = offerType
    this.#price = price
    this.#transmissionType = transmissionType
    this.#year = year
  }

  static createFromMakeWithModels({makeId, makeName, models = []}) {
    const modelsList = models.map(({icon, id, label}) => ({icon, id, name: label}))

    return new SpecificationsVehiclesValueObject({
      id: IdSharedValueObject.empty(),
      bodyType: BodyTypeSpecificationVehiclesValueObject.empty(),
      cubicCapacity: CubicCapacitySpecificationVehiclesValueObject.empty(),
      drivenWheels: DrivenWheelsSpecificationVehiclesValueObject.empty(),
      environmentalLabel: EnvironmentalLabelSpecificationsValueObject.empty(),
      fuelType: FuelTypeSpecificationVehiclesValueObject.empty(),
      hp: HpSpecificationVehiclesValueObject.empty(),
      images: ListImagesValueObject.empty(),
      km: KmSpecificationVehiclesValueObject.empty(),
      make: MakeSpecificationVehiclesValueObject.createFromLiterals({id: makeId, name: makeName}),
      model: ModelSpecificationVehiclesValueObject.empty(),
      models: ModelsSpecificationListVehiclesValueObject.createFromLiterals({models: modelsList}),
      offerType: OfferTypeSpecificationVehiclesValueObject.empty(),
      price: PriceSpecificationValueObject.empty(),
      transmissionType: TransmissionTypeSpecificationVehiclesValueObject.empty(),
      year: YearSpecificationVehiclesValueObject.empty()
    })
  }

  static createFromListingApi({
    bodyTypeId,
    cubicCapacity,
    drivenWheelsId,
    environmentalLabel,
    fuelTypeId,
    hasStock,
    hp,
    isCertified,
    km,
    makeId,
    modelId,
    offerTypeId,
    transmissionTypeId,
    year
  }) {
    return new SpecificationsVehiclesValueObject({
      id: IdSharedValueObject.empty(),
      bodyType: BodyTypeSpecificationVehiclesValueObject.createFromId({id: bodyTypeId}),
      cubicCapacity: CubicCapacitySpecificationVehiclesValueObject.createFromId({id: cubicCapacity}),
      drivenWheels: DrivenWheelsSpecificationVehiclesValueObject.createFromId({id: drivenWheelsId}),
      environmentalLabel: EnvironmentalLabelSpecificationsValueObject.createFromId({id: environmentalLabel}),
      fuelType: FuelTypeSpecificationVehiclesValueObject.createFromId({id: fuelTypeId}),
      hasStock,
      hp: HpSpecificationVehiclesValueObject.createFromId({id: hp}),
      images: ListImagesValueObject.empty(),
      isCertified,
      km: KmSpecificationVehiclesValueObject.createFromId({id: km}),
      make: MakeSpecificationVehiclesValueObject.createFromId({id: makeId}),
      model: ModelSpecificationVehiclesValueObject.createFromId({id: modelId}),
      models: ModelsSpecificationListVehiclesValueObject.empty(),
      offerType: OfferTypeSpecificationVehiclesValueObject.createFromId({id: offerTypeId}),
      price: PriceSpecificationValueObject.empty(),
      transmissionType: TransmissionTypeSpecificationVehiclesValueObject.createFromId({id: transmissionTypeId}),
      year: YearSpecificationVehiclesValueObject.createFromId({id: year})
    })
  }

  static createFromSuggestionApi(params) {
    const {
      makeId,
      make,
      modelId,
      model,
      powerInHP,
      fuelTypeId,
      bodyTypeId,
      year,
      gearTypeId,
      mainPicture,
      manufacturerPrice,
      uniqueVehicleId: id
    } = params
    return new SpecificationsVehiclesValueObject({
      id: IdSharedValueObject.createFromPrimitives({id}),
      bodyType: BodyTypeSpecificationVehiclesValueObject.createFromId({id: bodyTypeId}),
      cubicCapacity: CubicCapacitySpecificationVehiclesValueObject.empty(),
      drivenWheels: DrivenWheelsSpecificationVehiclesValueObject.empty(),
      environmentalLabel: EnvironmentalLabelSpecificationsValueObject.empty(),
      fuelType: FuelTypeSpecificationVehiclesValueObject.createFromId({id: fuelTypeId}),
      hp: HpSpecificationVehiclesValueObject.createFromId({id: powerInHP}),
      images: ListImagesValueObject.create({images: mainPicture ? [mainPicture] : []}),
      km: KmSpecificationVehiclesValueObject.empty(),
      make: MakeSpecificationVehiclesValueObject.createFromLiterals({id: makeId, name: make}),
      model: ModelSpecificationVehiclesValueObject.createFromLiterals({id: modelId, name: model}),
      models: ModelsSpecificationListVehiclesValueObject.empty(),
      offerType: OfferTypeSpecificationVehiclesValueObject.empty(),
      price: PriceSpecificationValueObject.create({price: manufacturerPrice}),
      transmissionType: TransmissionTypeSpecificationVehiclesValueObject.createFromId({id: gearTypeId}),
      year: YearSpecificationVehiclesValueObject.createFromId({id: year})
    })
  }

  get id() {
    return this.#id.value
  }

  get bodyType() {
    return this.#bodyType.value
  }

  get cubicCapacity() {
    return this.#cubicCapacity.value
  }

  get drivenWheels() {
    return this.#drivenWheels.value
  }

  get environmentalLabel() {
    return this.#environmentalLabel.value
  }

  get fuelType() {
    return this.#fuelType.value
  }

  get hasStock() {
    return this.#hasStock
  }

  get hp() {
    return this.#hp.value
  }

  get images() {
    return this.#images.value
  }

  get isCertified() {
    return this.#isCertified
  }

  get km() {
    return this.#km.value
  }

  get make() {
    return this.#make.value
  }

  get model() {
    return this.#model.value
  }

  get models() {
    return this.#models.value
  }

  get offerType() {
    return this.#offerType.value
  }

  get price() {
    return this.#price.value
  }

  get transmissionType() {
    return this.#transmissionType.value
  }

  get year() {
    return this.#year.value
  }

  get value() {
    return {
      ...(!this.#id.isEmpty() && {id: this.id}),
      ...(this.hasStock !== undefined && {hasStock: this.hasStock}),
      ...(this.isCertified !== undefined && {isCertified: this.isCertified}),
      ...(!this.#bodyType.isEmpty() && {bodyType: this.bodyType}),
      ...(!this.#cubicCapacity.isEmpty() && {cubicCapacity: this.cubicCapacity}),
      ...(!this.#drivenWheels.isEmpty() && {drivenWheels: this.drivenWheels}),
      ...(!this.#environmentalLabel.isEmpty() && {environmentalLabel: this.environmentalLabel}),
      ...(!this.#fuelType.isEmpty() && {fuelType: this.fuelType}),
      ...(!this.#hp.isEmpty() && {hp: this.hp}),
      ...(!this.#images?.isEmpty() && {images: this.images}),
      ...(!this.#km.isEmpty() && {km: this.km}),
      ...(!this.#make.isEmpty() && {make: this.make}),
      ...(!this.#model.isEmpty() && {model: this.model}),
      ...(!this.#models.isEmpty() && {models: this.models}),
      ...(!this.#offerType.isEmpty() && {offerType: this.offerType}),
      ...(!this.#price.isEmpty() && {price: this.price}),
      ...(!this.#transmissionType.isEmpty() && {transmissionType: this.transmissionType}),
      ...(!this.#year.isEmpty() && {year: this.year})
    }
  }
}
